import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { dashboardRouteToUrl } from 'blocks/dashboard/routes';
import React from 'react';
import { ServiceHttpError } from '../service_error';
import { Button } from './button';
import { Dialog } from './dialog';
import { formatError } from './form';
import { Rows } from './layout';
import { LoadingDots } from './loading_dots';
import { TickRow } from './tick_row';
import { TextRegularLight } from './typography';

export function GenericErrorDialog({
  title,
  error,
  dismiss,
}: {
  title: string;
  error: unknown;
  dismiss: (() => void) | 'reloadPage';
}) {
  const onClose =
    dismiss === 'reloadPage' ? () => window.location.reload() : dismiss;
  return (
    <Dialog
      title={title}
      onClose={onClose}
      buttons={
        <Button label="OK" variant="primary" onClick={onClose} size="large" />
      }
    >
      <TextRegularLight>{formatError(error)}</TextRegularLight>
      <TextRegularLight>
        {dismiss === 'reloadPage'
          ? 'Please reload the page to try again.'
          : 'If the error persists, please contact support.'}
      </TextRegularLight>
    </Dialog>
  );
}

export function getLimitErrorDialog(error: unknown) {
  if (!(error instanceof ServiceHttpError) || error.status !== 403) {
    return null;
  }
  if (error.toString().includes('reached free project limit')) {
    return FreeLimitDialog;
  }
  if (error.toString().includes('reached starter plan project limit')) {
    return StarterLimitDialog;
  }
}

export function FreeLimitDialog({ onClose }: { onClose?(): void }) {
  const upgradeHref = authRouteToUrl({
    path: 'upgrade',
    context: { redirect: getCurrentLocationForRedirect() },
  });

  return (
    <Dialog
      title="You’ve reached the limit"
      onClose={onClose || goToDashboard}
      buttons={
        <Button
          variant="primary"
          label="Upgrade plan"
          href={upgradeHref}
          size="large"
        />
      }
    >
      <TextRegularLight>
        You have reached the limit of 1 project storage on the Free plan. To
        create another sitemap, please upgrade your plan.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow>Unlock unlimited projects</TickRow>
        <TickRow>Get access to all Relume Library features</TickRow>
      </Rows>
    </Dialog>
  );
}

function StarterLimitDialog({ onClose }: { onClose?(): void }) {
  const upgradeHref = authRouteToUrl({
    path: 'upgrade',
    context: { redirect: getCurrentLocationForRedirect() },
  });

  return (
    <Dialog
      title="You’ve reached the limit"
      onClose={onClose || goToDashboard}
      buttons={
        <Button
          variant="primary"
          label="Upgrade plan"
          href={upgradeHref}
          size="large"
        />
      }
    >
      <TextRegularLight>
        You have reached the limit of 3 projects on the Starter plan. To create
        another sitemap, please upgrade your plan.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow>Unlock unlimited projects</TickRow>
        <TickRow>Get access to all Relume Library features</TickRow>
      </Rows>
    </Dialog>
  );
}

export function goToDashboard() {
  window.location.href = dashboardRouteToUrl({ path: 'dashboard' });
}

export function LoadingDialog({ title }: { title: string }) {
  return (
    <Dialog title={title}>
      <LoadingDots aspectRatio={4 / 1} />
    </Dialog>
  );
}
